import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    FaUserSlash,
    FaSync,
    FaSearch,
    FaTimes,
    FaSpinner,
    FaExclamationTriangle,
    FaCheckCircle,
    FaInfoCircle,
    FaCalendarAlt,
    FaUndo,
    FaUserCheck,
    FaClock,
    FaExclamationCircle,
    FaHistory,
} from 'react-icons/fa';
import {
    BLEEP_SERVER_NAMES,
    INACTIVITY_OUTCOMES,
    INACTIVITY_REASONS,
    INACTIVITY_ISSUE_LABELS,
    INACTIVITY_REPORT_VIEWS,
} from '../../utils/constants';
import {
    getEmployeeInactivityReport,
    saveEmployeeInactivityReport,
    runEmployeeInactivityDryRun,
    terminateEmployeeForInactivity,
    revertEmployeeInactivityTermination,
    getBleepEmployeeInactivityRow,
} from '../../services/onboardingService';

const OUTCOME_BADGE_STYLES = {
    KEEP_ACTIVE: { bg: '#def7ec', text: '#03543f', border: '#bcf0da', label: 'Keep active' },
    DUE_TO_TERMINATE: { bg: '#fef08a', text: '#854d0e', border: '#fde047', label: 'Due in 7 days' },
    WOULD_TERMINATE: { bg: '#fee2e2', text: '#991b1b', border: '#fca5a5', label: 'Would terminate' },
    NEEDS_REVIEW: { bg: '#f3e8ff', text: '#6b21a8', border: '#d8b4fe', label: 'Needs review' },
    ALREADY_INACTIVE: { bg: '#f1f5f9', text: '#475569', border: '#cbd5e1', label: 'Already inactive' },
};

const formatDate = (val, showTime = false) => {
    if (!val) return 'Not recorded';
    const d = new Date(val);
    if (isNaN(d.getTime())) return String(val);
    const dateStr = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    if (!showTime) return dateStr;
    const timeStr = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
    return `${dateStr} · ${timeStr} UK`;
};

const EmployeeInactivityTab = ({ serverID }) => {
    const isCAMHS = serverID === '360camhs.co.uk';

    const [rows, setRows] = useState([]);
    const [running, setRunning] = useState(false);
    const [actionBusy, setActionBusy] = useState(false);
    const [error, setError] = useState(null);
    const [toast, setToast] = useState(null);

    // Filter states
    const [selectedFilter, setSelectedFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    // Metadata from report run
    const [evaluatedAt, setEvaluatedAt] = useState(null);
    const [reportingPeriod, setReportingPeriod] = useState(null);
    const [reportRunID, setReportRunID] = useState(null);
    const [savedReportNotice, setSavedReportNotice] = useState(null);
    const [dryRunNotice, setDryRunNotice] = useState(null);

    // Action modals state
    const [terminateTarget, setTerminateTarget] = useState(null);
    const [revertTarget, setRevertTarget] = useState(null);
    const [actionOutcomeModal, setActionOutcomeModal] = useState(null);

    // Debounce search input by 300ms
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchQuery);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    // Auto-dismiss toast
    useEffect(() => {
        if (!toast) return;
        const timer = setTimeout(() => setToast(null), 6000);
        return () => clearTimeout(timer);
    }, [toast]);

    /**
     * Loads saved inactivity report (CAMHS only).
     * Paginates through cursor `afterEmployeeID` to collect all rows with integrity checks.
     */
    const loadSavedReport = useCallback(async (generateFresh = false) => {
        if (!isCAMHS || running || actionBusy) return;

        setRunning(true);
        setError(null);
        setDryRunNotice(null);
        setSavedReportNotice(
            generateFresh
                ? 'Scanning employees and generating a new server report (this may take a few minutes)…'
                : 'Loading the latest completed saved report…'
        );

        try {
            if (generateFresh) {
                const saveRes = await saveEmployeeInactivityReport(serverID);
                if (saveRes?.alreadyRunning === true) {
                    setSavedReportNotice('Another report generation is currently running. Please wait a moment and reload.');
                    setRunning(false);
                    return;
                }
            }

            const loadedRows = [];
            const seenEmployees = new Set();
            const seenCursors = new Set();
            let runID = null;
            let cursor = null;
            let reportEvalAt = null;
            let reportPeriod = null;

            while (true) {
                const res = await getEmployeeInactivityReport(serverID, runID, cursor);
                if (res?.success !== true || res?.serverID !== serverID) {
                    throw new Error('Unexpected response while fetching inactivity report.');
                }

                if (res?.found !== true) {
                    setSavedReportNotice('No saved report found yet. Press "Scan & Save Report" to create the first baseline.');
                    setRows([]);
                    break;
                }

                if (!runID) {
                    runID = res.runID;
                    reportEvalAt = res.evaluatedAt;
                    reportPeriod = res.reportingPeriod;
                } else if (res.runID !== runID) {
                    throw new Error('Report version changed during pagination.');
                }

                for (const raw of res.rows || []) {
                    const empID = raw.employeeID;
                    if (!empID || seenEmployees.has(empID)) {
                        continue;
                    }
                    seenEmployees.add(empID);
                    loadedRows.push(raw);
                }

                if (res.hasMore === false) {
                    break;
                }

                const next = res.nextEmployeeID;
                if (!next || seenCursors.has(next)) {
                    break;
                }
                seenCursors.add(next);
                cursor = next;
            }

            setRows(loadedRows);
            setReportRunID(runID);
            setEvaluatedAt(reportEvalAt);
            setReportingPeriod(reportPeriod);
            setSavedReportNotice(null);
        } catch (err) {
            console.error('Failed to load saved inactivity report:', err);
            setError(err?.message || 'Failed to load saved inactivity report.');
            setSavedReportNotice(null);
        } finally {
            setRunning(false);
        }
    }, [isCAMHS, running, actionBusy, serverID]);

    /**
     * Executes a live, non-destructive read-only assessment across any Bleep franchise.
     */
    const handleRunDryRun = async () => {
        if (running || actionBusy) return;

        setRunning(true);
        setError(null);
        setSavedReportNotice(null);
        setDryRunNotice('Executing live read-only activity assessment across shifts and visits…');

        try {
            const res = await runEmployeeInactivityDryRun(serverID);
            if (res?.success !== true) {
                throw new Error('Inactivity live dry-run failed.');
            }

            const incomingRows = res.rows || [];
            setRows(incomingRows);
            setEvaluatedAt(res.evaluatedAt);
            setReportingPeriod(res.reportingPeriod);
            setReportRunID(null); // Dry runs are not saved to a runID document
            setDryRunNotice(`Live assessment complete. Evaluated ${incomingRows.length} active employee records.`);
            setToast({
                type: 'success',
                message: `Assessed ${incomingRows.length} employees on ${BLEEP_SERVER_NAMES[serverID] || serverID}.`,
            });
        } catch (err) {
            console.error('Inactivity dry-run error:', err);
            setError(err?.message || 'Failed to execute live inactivity assessment.');
            setDryRunNotice(null);
        } finally {
            setRunning(false);
        }
    };

    // Auto-load saved report for CAMHS on mount
    useEffect(() => {
        if (isCAMHS) {
            loadSavedReport(false);
        } else {
            // For non-CAMHS servers, reset to blank state awaiting user manual scan
            setRows([]);
            setEvaluatedAt(null);
            setReportingPeriod(null);
            setReportRunID(null);
        }
    }, [serverID]);

    /**
     * Refreshes a single employee's assessment row on demand without re-scanning everyone.
     */
    const handleRefreshRow = async (employeeID) => {
        if (actionBusy) return;
        try {
            const res = await getBleepEmployeeInactivityRow(serverID, employeeID);
            if (res?.success === true && res?.row) {
                setRows((prev) =>
                    prev.map((r) => (r.employeeID === employeeID ? { ...r, ...res.row } : r))
                );
                setToast({
                    type: 'success',
                    message: `Refreshed evaluation for employee ${employeeID}.`,
                });
            }
        } catch (err) {
            console.error(`Failed to refresh employee ${employeeID}:`, err);
            setToast({
                type: 'error',
                message: err?.message || `Failed to refresh employee ${employeeID}.`,
            });
        }
    };

    /**
     * Executes termination for an inactive employee.
     */
    const handleConfirmTerminate = async () => {
        if (!terminateTarget || actionBusy) return;
        const empID = terminateTarget.employeeID;
        const targetServer = serverID;

        setActionBusy(true);
        setError(null);

        try {
            const res = await terminateEmployeeForInactivity(targetServer, empID);
            setTerminateTarget(null);

            if (res?.success !== true || !['TERMINATED', 'SKIPPED'].includes(res?.outcome)) {
                throw new Error('Unexpected response during termination.');
            }

            setActionOutcomeModal({
                title: res.outcome === 'TERMINATED' ? 'Employee Terminated' : 'Termination Skipped',
                employeeID: empID,
                outcome: res.outcome,
                reason: res.reason,
                logID: res.logID,
                message:
                    res.outcome === 'TERMINATED'
                        ? `Statuses set to inactive. Termination log: ${res.logID}. You may restore this record under "Already inactive" if required.`
                        : `No status changes were made. Reason: ${INACTIVITY_REASONS[res.reason] || res.reason}`,
            });

            // Refresh report
            if (isCAMHS) {
                loadSavedReport(false);
            } else {
                handleRunDryRun();
            }
        } catch (err) {
            console.error('Termination execution error:', err);
            setError(err?.message || `Failed to terminate employee ${empID}.`);
            setTerminateTarget(null);
        } finally {
            setActionBusy(false);
        }
    };

    /**
     * Reverts an inactive employee termination back to their baseline status.
     */
    const handleConfirmRevert = async () => {
        if (!revertTarget || actionBusy) return;
        const empID = revertTarget.employeeID;
        const logID = revertTarget.terminationLogID;
        const targetServer = serverID;

        setActionBusy(true);
        setError(null);

        try {
            const res = await revertEmployeeInactivityTermination(targetServer, empID, logID);
            setRevertTarget(null);

            if (res?.success !== true || !['REVERTED', 'ALREADY_REVERTED'].includes(res?.outcome)) {
                throw new Error('Unexpected response during termination reversal.');
            }

            setActionOutcomeModal({
                title: 'Termination Reverted',
                employeeID: empID,
                outcome: res.outcome,
                reason: res.outcome === 'ALREADY_REVERTED' ? 'Already restored in a prior action.' : 'Successfully restored.',
                logID: logID,
                message: `Employee ${empID} statuses restored to active baseline. Audit entry recorded.`,
            });

            // Refresh report
            if (isCAMHS) {
                loadSavedReport(false);
            } else {
                handleRunDryRun();
            }
        } catch (err) {
            console.error('Revert execution error:', err);
            setError(err?.message || `Failed to revert termination for employee ${empID}.`);
            setRevertTarget(null);
        } finally {
            setActionBusy(false);
        }
    };

    /**
     * Evaluates category matching per row (matches Flutter _matchesView).
     */
    const matchesView = (row, view) => {
        if (view === 'all') return true;

        const details = row.details || {};

        if (view === 'NEWLY_ADDED') {
            return details.newlyAddedInReportingPeriod === true;
        }

        const isTerminationSubView = [
            'RECENTLY_TERMINATED',
            'LONG_TERM_TERMINATED',
            'TERMINATION_DATE_UNKNOWN',
        ].includes(view);

        if (!isTerminationSubView) {
            return row.outcome === view;
        }

        if (row.outcome !== 'ALREADY_INACTIVE') return false;

        const termination = details.termination || {};
        const termDateStr = termination.terminatedAt || '';
        const evalDateStr = evaluatedAt || '';

        const terminatedAt = termDateStr ? new Date(termDateStr).getTime() : NaN;
        const evalAt = evalDateStr ? new Date(evalDateStr).getTime() : NaN;

        const hasReliableDate =
            termination.verified === true &&
            !termination.revertedAt &&
            !isNaN(terminatedAt) &&
            !isNaN(evalAt) &&
            terminatedAt <= evalAt;

        if (view === 'TERMINATION_DATE_UNKNOWN') {
            return !hasReliableDate;
        }

        if (!hasReliableDate) return false;

        const diffDays = (evalAt - terminatedAt) / (1000 * 60 * 60 * 24);

        if (view === 'RECENTLY_TERMINATED') {
            return diffDays <= 90;
        }

        return diffDays > 90;
    };

    // Live counts for all chips
    const filterCounts = useMemo(() => {
        const counts = {
            all: rows.length,
            KEEP_ACTIVE: 0,
            DUE_TO_TERMINATE: 0,
            WOULD_TERMINATE: 0,
            NEEDS_REVIEW: 0,
            ALREADY_INACTIVE: 0,
            NEWLY_ADDED: 0,
            RECENTLY_TERMINATED: 0,
            LONG_TERM_TERMINATED: 0,
            TERMINATION_DATE_UNKNOWN: 0,
        };

        rows.forEach((row) => {
            if (counts[row.outcome] !== undefined) {
                counts[row.outcome]++;
            }
            if (matchesView(row, 'NEWLY_ADDED')) counts.NEWLY_ADDED++;
            if (matchesView(row, 'RECENTLY_TERMINATED')) counts.RECENTLY_TERMINATED++;
            if (matchesView(row, 'LONG_TERM_TERMINATED')) counts.LONG_TERM_TERMINATED++;
            if (matchesView(row, 'TERMINATION_DATE_UNKNOWN')) counts.TERMINATION_DATE_UNKNOWN++;
        });

        return counts;
    }, [rows, evaluatedAt]);

    // Filtered items based on filter chips and search
    const filteredRows = useMemo(() => {
        const q = debouncedSearch.trim().toLowerCase();

        return rows.filter((row) => {
            // Outcome / category view filter
            if (!matchesView(row, selectedFilter)) return false;

            // Search filter
            if (q) {
                const name = (row.name || '').toLowerCase();
                const email = (row.email || '').toLowerCase();
                const empID = (row.employeeID || '').toLowerCase();
                const searchable = `${name} ${email} ${empID}`;
                if (!searchable.includes(q)) return false;
            }

            return true;
        });
    }, [rows, selectedFilter, debouncedSearch, evaluatedAt]);

    return (
        <div>
            {/* Feedback Toast */}
            {toast && (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 18px',
                    borderRadius: '8px',
                    marginBottom: '16px',
                    fontSize: '14px',
                    fontWeight: '600',
                    backgroundColor: toast.type === 'success' ? '#def7ec' : '#fde8e8',
                    color: toast.type === 'success' ? '#03543f' : '#9b1c1c',
                    border: `1px solid ${toast.type === 'success' ? '#bcf0da' : '#f8b4b4'}`,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {toast.type === 'success' ? <FaCheckCircle /> : <FaExclamationCircle />}
                        <span>{toast.message}</span>
                    </div>
                    <button
                        type="button"
                        onClick={() => setToast(null)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}
                    >
                        <FaTimes />
                    </button>
                </div>
            )}

            {/* Inactivity Header & Actions Bar */}
            <div style={{
                backgroundColor: '#ffffff',
                borderRadius: '10px',
                border: '1px solid #e2e8f0',
                padding: '20px',
                marginBottom: '20px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    flexWrap: 'wrap',
                    gap: '16px',
                    marginBottom: '16px',
                }}>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#2d3748' }}>
                                Employee Inactivity Maintenance
                            </h2>
                            <span style={{
                                backgroundColor: '#edf2f7',
                                color: '#2b6cb0',
                                padding: '3px 10px',
                                borderRadius: '12px',
                                fontSize: '12px',
                                fontWeight: '700',
                            }}>
                                {BLEEP_SERVER_NAMES[serverID] || serverID}
                            </span>
                        </div>
                        <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#718096' }}>
                            Identify employees with no qualifying bookings over 30 days and manage safe termination workflows.
                        </p>
                    </div>

                    {/* Action buttons */}
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                        {isCAMHS ? (
                            <>
                                <button
                                    type="button"
                                    onClick={() => loadSavedReport(false)}
                                    disabled={running || actionBusy}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        backgroundColor: '#edf2f7',
                                        color: '#2b6cb0',
                                        border: '1px solid #cbd5e0',
                                        padding: '9px 15px',
                                        borderRadius: '6px',
                                        fontSize: '13px',
                                        fontWeight: '600',
                                        cursor: running ? 'not-allowed' : 'pointer',
                                    }}
                                >
                                    <FaSync className={running ? 'fa-spin' : ''} />
                                    <span>Reload Saved Report</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => loadSavedReport(true)}
                                    disabled={running || actionBusy}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        backgroundColor: '#2b6cb0',
                                        color: '#ffffff',
                                        border: 'none',
                                        padding: '9px 16px',
                                        borderRadius: '6px',
                                        fontSize: '13px',
                                        fontWeight: '600',
                                        cursor: running ? 'not-allowed' : 'pointer',
                                        boxShadow: '0 2px 4px rgba(43, 108, 176, 0.2)',
                                    }}
                                >
                                    {running ? <FaSpinner className="fa-spin" /> : <FaClock />}
                                    <span>Scan & Save New Report</span>
                                </button>
                            </>
                        ) : (
                            <button
                                type="button"
                                onClick={handleRunDryRun}
                                disabled={running || actionBusy}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    backgroundColor: '#2b6cb0',
                                    color: '#ffffff',
                                    border: 'none',
                                    padding: '9px 18px',
                                    borderRadius: '6px',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    cursor: running ? 'not-allowed' : 'pointer',
                                }}
                            >
                                {running ? <FaSpinner className="fa-spin" /> : <FaSync />}
                                <span>Run Live Assessment Scan</span>
                            </button>
                        )}
                    </div>
                </div>

                {/* Reporting Period & Run Metadata Bar */}
                {(evaluatedAt || reportingPeriod) && (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: '16px',
                        padding: '10px 14px',
                        backgroundColor: '#f8fafc',
                        borderRadius: '6px',
                        border: '1px solid #e2e8f0',
                        fontSize: '12px',
                        color: '#4a5568',
                    }}>
                        <div>
                            <span style={{ color: '#718096' }}>Evaluated at: </span>
                            <strong>{formatDate(evaluatedAt, true)}</strong>
                        </div>
                        {reportingPeriod && (
                            <div>
                                <span style={{ color: '#718096' }}>Reporting period: </span>
                                <strong>
                                    {formatDate(reportingPeriod.startDate)} to {formatDate(reportingPeriod.endDateExclusive)} (UK Midnight, End Excluded)
                                </strong>
                            </div>
                        )}
                        {reportRunID && (
                            <div>
                                <span style={{ color: '#718096' }}>Run ID: </span>
                                <code style={{ backgroundColor: '#edf2f7', padding: '2px 5px', borderRadius: '4px' }}>
                                    {reportRunID}
                                </code>
                            </div>
                        )}
                    </div>
                )}

                {/* Status Notice */}
                {savedReportNotice && (
                    <div style={{ marginTop: '12px', padding: '10px 14px', backgroundColor: '#eff6ff', color: '#1e40af', borderRadius: '6px', fontSize: '13px' }}>
                        <FaInfoCircle style={{ marginRight: '6px' }} />
                        <span>{savedReportNotice}</span>
                    </div>
                )}
                {dryRunNotice && (
                    <div style={{ marginTop: '12px', padding: '10px 14px', backgroundColor: '#f0fdf4', color: '#166534', borderRadius: '6px', fontSize: '13px' }}>
                        <FaCheckCircle style={{ marginRight: '6px' }} />
                        <span>{dryRunNotice}</span>
                    </div>
                )}

                {/* Error Banner */}
                {error && (
                    <div style={{ marginTop: '12px', padding: '12px 14px', backgroundColor: '#fef2f2', color: '#991b1b', borderRadius: '6px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <FaExclamationTriangle />
                        <span>{error}</span>
                    </div>
                )}

                {/* Search Bar */}
                <div style={{ marginTop: '16px', position: 'relative' }}>
                    <FaSearch style={{
                        position: 'absolute',
                        left: '14px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#a0aec0',
                        fontSize: '14px',
                    }} />
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search employee name, email, or employee ID…"
                        style={{
                            width: '100%',
                            padding: '10px 40px 10px 38px',
                            fontSize: '14px',
                            border: '1px solid #cbd5e0',
                            borderRadius: '6px',
                            outline: 'none',
                            backgroundColor: '#f7fafc',
                            boxSizing: 'border-box',
                            color: '#2d3748',
                        }}
                    />
                    {searchQuery && (
                        <button
                            type="button"
                            onClick={() => setSearchQuery('')}
                            style={{
                                position: 'absolute',
                                right: '12px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                background: 'none',
                                border: 'none',
                                color: '#a0aec0',
                                cursor: 'pointer',
                                fontSize: '14px',
                            }}
                        >
                            <FaTimes />
                        </button>
                    )}
                </div>

                {/* Primary Outcome Filter Chips */}
                <div style={{ marginTop: '16px' }}>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                        <span style={{ fontSize: '13px', fontWeight: '700', color: '#718096', marginRight: '4px' }}>
                            Outcomes:
                        </span>
                        {Object.entries(INACTIVITY_OUTCOMES).map(([key, label]) => {
                            const isSelected = selectedFilter === key;
                            const count = filterCounts[key] ?? 0;
                            return (
                                <button
                                    key={key}
                                    type="button"
                                    onClick={() => setSelectedFilter(key)}
                                    style={{
                                        padding: '6px 12px',
                                        borderRadius: '20px',
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        border: `1px solid ${isSelected ? '#2b6cb0' : '#e2e8f0'}`,
                                        backgroundColor: isSelected ? '#ebf8ff' : '#ffffff',
                                        color: isSelected ? '#2b6cb0' : '#4a5568',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        transition: 'all 0.15s ease',
                                    }}
                                >
                                    <span>{label}</span>
                                    <span style={{
                                        backgroundColor: isSelected ? '#2b6cb0' : '#edf2f7',
                                        color: isSelected ? '#ffffff' : '#718096',
                                        borderRadius: '10px',
                                        padding: '1px 7px',
                                        fontSize: '11px',
                                    }}>
                                        {count}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Secondary Sub-Category Views */}
                <div style={{ marginTop: '12px', borderTop: '1px solid #edf2f7', paddingTop: '12px' }}>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                        <span style={{ fontSize: '12px', fontWeight: '700', color: '#a0aec0', marginRight: '4px' }}>
                            Sub-Categories:
                        </span>
                        {Object.entries(INACTIVITY_REPORT_VIEWS).map(([key, label]) => {
                            const isSelected = selectedFilter === key;
                            const count = filterCounts[key] ?? 0;
                            return (
                                <button
                                    key={key}
                                    type="button"
                                    onClick={() => setSelectedFilter(key)}
                                    style={{
                                        padding: '4px 10px',
                                        borderRadius: '6px',
                                        fontSize: '12px',
                                        fontWeight: '500',
                                        cursor: 'pointer',
                                        border: `1px solid ${isSelected ? '#718096' : '#edf2f7'}`,
                                        backgroundColor: isSelected ? '#f1f5f9' : '#ffffff',
                                        color: isSelected ? '#1e293b' : '#64748b',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                    }}
                                >
                                    <span>{label}</span>
                                    <span style={{
                                        backgroundColor: isSelected ? '#cbd5e1' : '#f1f5f9',
                                        color: '#475569',
                                        borderRadius: '4px',
                                        padding: '1px 5px',
                                        fontSize: '11px',
                                    }}>
                                        {count}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Results Section */}
            {running ? (
                <div style={{
                    backgroundColor: '#ffffff',
                    borderRadius: '10px',
                    border: '1px solid #e2e8f0',
                    padding: '60px 20px',
                    textAlign: 'center',
                }}>
                    <FaSpinner className="fa-spin" style={{ fontSize: '32px', color: '#2b6cb0', marginBottom: '16px' }} />
                    <p style={{ margin: 0, color: '#4a5568', fontSize: '15px', fontWeight: '600' }}>
                        Evaluating employee records for {BLEEP_SERVER_NAMES[serverID] || serverID}…
                    </p>
                    <p style={{ margin: '6px 0 0 0', color: '#a0aec0', fontSize: '13px' }}>
                        Scanning activity limits, booking records, and authentication states.
                    </p>
                </div>
            ) : filteredRows.length === 0 ? (
                <div style={{
                    backgroundColor: '#ffffff',
                    borderRadius: '10px',
                    border: '1px solid #e2e8f0',
                    padding: '60px 20px',
                    textAlign: 'center',
                }}>
                    <FaUserCheck style={{ fontSize: '36px', color: '#cbd5e0', marginBottom: '12px' }} />
                    <h3 style={{ margin: '0 0 6px 0', color: '#4a5568', fontSize: '16px', fontWeight: '700' }}>
                        {rows.length === 0 ? 'No Inactivity Assessment Loaded' : 'No Records Match Filters'}
                    </h3>
                    <p style={{ margin: 0, color: '#718096', fontSize: '13px' }}>
                        {rows.length === 0
                            ? (isCAMHS ? 'Press "Scan & Save New Report" above to run an evaluation.' : 'Press "Run Live Assessment Scan" above to evaluate staff.')
                            : 'Try adjusting your search query or selecting a different outcome filter.'}
                    </p>
                </div>
            ) : (
                <div>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '12px',
                        padding: '0 4px',
                        fontSize: '13px',
                        color: '#718096',
                        fontWeight: '600',
                    }}>
                        <span>Showing {filteredRows.length} of {rows.length} evaluated employees</span>
                        <span>Server: {BLEEP_SERVER_NAMES[serverID] || serverID}</span>
                    </div>

                    {filteredRows.map((row) => {
                        const empID = row.employeeID;
                        const badge = OUTCOME_BADGE_STYLES[row.outcome] || OUTCOME_BADGE_STYLES.NEEDS_REVIEW;
                        const reasonText = INACTIVITY_REASONS[row.reason] || row.reason || 'No specific explanation recorded.';
                        const evidence = row.evidence || {};
                        const issues = Array.isArray(row.issues) ? row.issues : [];
                        const details = row.details || {};
                        const terminationLogID = details.termination?.logID || row.terminationLogID;

                        const canTerminate = row.outcome === 'WOULD_TERMINATE';
                        const canRevert = row.outcome === 'ALREADY_INACTIVE' && Boolean(terminationLogID);

                        return (
                            <div
                                key={empID}
                                style={{
                                    backgroundColor: '#ffffff',
                                    borderRadius: '10px',
                                    border: '1px solid #e2e8f0',
                                    padding: '18px 20px',
                                    marginBottom: '14px',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                                    transition: 'all 0.15s ease',
                                }}
                            >
                                {/* Header Row */}
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'flex-start',
                                    flexWrap: 'wrap',
                                    gap: '12px',
                                    marginBottom: '12px',
                                }}>
                                    <div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#2d3748' }}>
                                                {row.name || 'Unnamed Employee'}
                                            </h3>
                                            <code style={{ backgroundColor: '#edf2f7', padding: '2px 7px', borderRadius: '4px', fontSize: '12px', color: '#4a5568' }}>
                                                {empID}
                                            </code>
                                        </div>
                                        <div style={{ fontSize: '13px', color: '#718096', marginTop: '3px' }}>
                                            {row.email || 'Email not recorded'}
                                        </div>
                                    </div>

                                    {/* Outcome Badge & Refresh Button */}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <span style={{
                                            backgroundColor: badge.bg,
                                            color: badge.text,
                                            border: `1px solid ${badge.border}`,
                                            padding: '4px 12px',
                                            borderRadius: '12px',
                                            fontSize: '12px',
                                            fontWeight: '700',
                                        }}>
                                            {badge.label}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => handleRefreshRow(empID)}
                                            disabled={actionBusy}
                                            title="Re-check this employee"
                                            style={{
                                                background: 'none',
                                                border: '1px solid #cbd5e0',
                                                borderRadius: '6px',
                                                padding: '4px 8px',
                                                cursor: 'pointer',
                                                color: '#718096',
                                                fontSize: '12px',
                                            }}
                                        >
                                            <FaSync />
                                        </button>
                                    </div>
                                </div>

                                {/* Reason Explanation */}
                                <div style={{
                                    padding: '10px 14px',
                                    backgroundColor: '#f8fafc',
                                    borderRadius: '6px',
                                    fontSize: '13px',
                                    color: '#2d3748',
                                    marginBottom: '12px',
                                    borderLeft: `4px solid ${badge.text}`,
                                }}>
                                    <strong>Assessment:</strong> {reasonText}
                                </div>

                                {/* Evidence Timeline Block */}
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                                    gap: '12px',
                                    padding: '12px 14px',
                                    backgroundColor: '#ffffff',
                                    borderRadius: '6px',
                                    border: '1px solid #edf2f7',
                                    fontSize: '12px',
                                    marginBottom: '12px',
                                }}>
                                    <div>
                                        <div style={{ color: '#718096', marginBottom: '2px' }}>Start / Baseline Date</div>
                                        <div style={{ fontWeight: '600', color: '#2d3748' }}>
                                            {formatDate(evidence.baselineDate || details.startDate)}
                                        </div>
                                    </div>
                                    <div>
                                        <div style={{ color: '#718096', marginBottom: '2px' }}>Last Qualifying Activity</div>
                                        <div style={{ fontWeight: '600', color: '#2d3748' }}>
                                            {formatDate(evidence.lastActivityDate)}
                                        </div>
                                    </div>
                                    <div>
                                        <div style={{ color: '#718096', marginBottom: '2px' }}>Next Scheduled Booking</div>
                                        <div style={{ fontWeight: '600', color: '#2d3748' }}>
                                            {formatDate(evidence.nextBooking)}
                                        </div>
                                    </div>
                                    <div>
                                        <div style={{ color: '#718096', marginBottom: '2px' }}>Days Remaining</div>
                                        <div style={{
                                            fontWeight: '700',
                                            color: (evidence.daysRemaining ?? 0) <= 7 ? '#c53030' : '#2b6cb0',
                                        }}>
                                            {evidence.daysRemaining !== undefined ? `${evidence.daysRemaining} days` : '—'}
                                        </div>
                                    </div>
                                </div>

                                {/* Warning Issues Tags */}
                                {issues.length > 0 && (
                                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '12px' }}>
                                        {issues.map((iss) => (
                                            <span
                                                key={iss}
                                                style={{
                                                    backgroundColor: '#fffbeb',
                                                    color: '#b45309',
                                                    border: '1px solid #fde68a',
                                                    padding: '3px 8px',
                                                    borderRadius: '4px',
                                                    fontSize: '11px',
                                                    fontWeight: '600',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '4px',
                                                }}
                                            >
                                                <FaExclamationTriangle style={{ fontSize: '10px' }} />
                                                <span>{INACTIVITY_ISSUE_LABELS[iss] || iss}</span>
                                            </span>
                                        ))}
                                    </div>
                                )}

                                {/* Action Buttons */}
                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
                                    {canTerminate && (
                                        <button
                                            type="button"
                                            onClick={() => setTerminateTarget(row)}
                                            disabled={actionBusy}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px',
                                                backgroundColor: '#dc2626',
                                                color: '#ffffff',
                                                border: 'none',
                                                padding: '7px 14px',
                                                borderRadius: '6px',
                                                fontSize: '12px',
                                                fontWeight: '700',
                                                cursor: actionBusy ? 'not-allowed' : 'pointer',
                                                boxShadow: '0 1px 3px rgba(220, 38, 38, 0.3)',
                                            }}
                                        >
                                            <FaUserSlash />
                                            <span>Terminate for Inactivity</span>
                                        </button>
                                    )}

                                    {canRevert && (
                                        <button
                                            type="button"
                                            onClick={() => setRevertTarget({ employeeID: empID, terminationLogID })}
                                            disabled={actionBusy}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px',
                                                backgroundColor: '#475569',
                                                color: '#ffffff',
                                                border: 'none',
                                                padding: '7px 14px',
                                                borderRadius: '6px',
                                                fontSize: '12px',
                                                fontWeight: '700',
                                                cursor: actionBusy ? 'not-allowed' : 'pointer',
                                            }}
                                        >
                                            <FaUndo />
                                            <span>Revert Termination</span>
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* TWO-STEP CONFIRMATION MODAL: TERMINATE EMPLOYEE */}
            {terminateTarget && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000,
                    padding: '20px',
                }}>
                    <div style={{
                        backgroundColor: '#ffffff',
                        borderRadius: '10px',
                        maxWidth: '520px',
                        width: '100%',
                        padding: '24px',
                        boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#dc2626', marginBottom: '12px' }}>
                            <FaExclamationTriangle style={{ fontSize: '24px' }} />
                            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700' }}>
                                Confirm Inactivity Termination
                            </h3>
                        </div>
                        <p style={{ color: '#4a5568', fontSize: '14px', lineHeight: '1.5' }}>
                            Are you sure you want to terminate <strong>{terminateTarget.name || terminateTarget.employeeID}</strong> on <strong>{BLEEP_SERVER_NAMES[serverID] || serverID}</strong>?
                        </p>
                        <div style={{
                            padding: '12px 14px',
                            backgroundColor: '#fff5f5',
                            borderRadius: '6px',
                            border: '1px solid #fed7d7',
                            fontSize: '13px',
                            color: '#991b1b',
                            marginBottom: '16px',
                        }}>
                            <strong>Safety Notice:</strong> Both the internal employee status and the authentication account will be marked inactive (<code>status: false</code>). The termination will be logged in the permanent audit trail.
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                            <button
                                type="button"
                                onClick={() => setTerminateTarget(null)}
                                disabled={actionBusy}
                                style={{
                                    backgroundColor: '#edf2f7',
                                    color: '#4a5568',
                                    border: 'none',
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleConfirmTerminate}
                                disabled={actionBusy}
                                style={{
                                    backgroundColor: '#dc2626',
                                    color: '#ffffff',
                                    border: 'none',
                                    padding: '8px 18px',
                                    borderRadius: '6px',
                                    fontSize: '13px',
                                    fontWeight: '700',
                                    cursor: actionBusy ? 'not-allowed' : 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                }}
                            >
                                {actionBusy ? <FaSpinner className="fa-spin" /> : <FaUserSlash />}
                                <span>Confirm Termination</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* CONFIRMATION MODAL: REVERT TERMINATION */}
            {revertTarget && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000,
                    padding: '20px',
                }}>
                    <div style={{
                        backgroundColor: '#ffffff',
                        borderRadius: '10px',
                        maxWidth: '520px',
                        width: '100%',
                        padding: '24px',
                        boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#2b6cb0', marginBottom: '12px' }}>
                            <FaHistory style={{ fontSize: '22px' }} />
                            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700' }}>
                                Revert Employee Termination?
                            </h3>
                        </div>
                        <p style={{ color: '#4a5568', fontSize: '14px', lineHeight: '1.5' }}>
                            Restore the employee and authentication statuses for employee <strong>{revertTarget.employeeID}</strong> on <strong>{BLEEP_SERVER_NAMES[serverID] || serverID}</strong>.
                        </p>
                        <div style={{
                            padding: '10px 14px',
                            backgroundColor: '#f8fafc',
                            borderRadius: '6px',
                            border: '1px solid #e2e8f0',
                            fontSize: '12px',
                            color: '#64748b',
                            marginBottom: '16px',
                        }}>
                            <div><strong>Termination Log ID:</strong> {revertTarget.terminationLogID}</div>
                            <div style={{ marginTop: '4px' }}>The server checks the restoration backup and current account fields. The reversal remains in the permanent audit trail.</div>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                            <button
                                type="button"
                                onClick={() => setRevertTarget(null)}
                                disabled={actionBusy}
                                style={{
                                    backgroundColor: '#edf2f7',
                                    color: '#4a5568',
                                    border: 'none',
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleConfirmRevert}
                                disabled={actionBusy}
                                style={{
                                    backgroundColor: '#2b6cb0',
                                    color: '#ffffff',
                                    border: 'none',
                                    padding: '8px 18px',
                                    borderRadius: '6px',
                                    fontSize: '13px',
                                    fontWeight: '700',
                                    cursor: actionBusy ? 'not-allowed' : 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                }}
                            >
                                {actionBusy ? <FaSpinner className="fa-spin" /> : <FaUndo />}
                                <span>Confirm Reversal</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ACTION OUTCOME MODAL */}
            {actionOutcomeModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000,
                    padding: '20px',
                }}>
                    <div style={{
                        backgroundColor: '#ffffff',
                        borderRadius: '10px',
                        maxWidth: '480px',
                        width: '100%',
                        padding: '24px',
                        boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
                    }}>
                        <h3 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: '700', color: '#2d3748' }}>
                            {actionOutcomeModal.title}
                        </h3>
                        <p style={{ color: '#4a5568', fontSize: '14px', lineHeight: '1.5', whiteSpace: 'pre-line' }}>
                            {actionOutcomeModal.message}
                        </p>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
                            <button
                                type="button"
                                onClick={() => setActionOutcomeModal(null)}
                                style={{
                                    backgroundColor: '#2b6cb0',
                                    color: '#ffffff',
                                    border: 'none',
                                    padding: '8px 18px',
                                    borderRadius: '6px',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                }}
                            >
                                Close & Refresh
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EmployeeInactivityTab;
